import { i18n } from '@/plugins/i18n'
import { storeService } from '@/services/api'
import { StoreUserRelationStatus, StoreStatus } from '@/constants'
import { groupByArrayOfObject, getFirstOfArray } from '@/common/arrays'

const initialStates = {
    status: 'LOADING',
    storesList: {},
    pagination: {
        no_paginate: false,
        per_page: 20,
        current_page: 1,
        last_page: 1,
    },
    params: {
        page: 1
    },
    tags: {}
}

const mutations = {
    loading(state) {
        state.status = 'LOADING'
    },
    setStoresList(state, stores) {
        state.status = 'SET_LIST'
        state.storesList = stores
    },
    pushStoresList(state, stores) {
        state.status = 'SET_LIST'
        state.storesList = Object.assign({}, state.storesList, stores)
    },
    setPagination(state, pagination) {
        state.status = 'SET_PAGINATION'
        state.pagination = Object.assign({}, state.pagination, pagination)
    },
    setParams(state, params) {
        state.status = 'SET_PARAMS'
        state.params = Object.assign({}, state.params, params)
    },
    resetParams(state) {
        state.status = 'RESET_PARAMS'
        state.params = {
            page: 1
        }
    },
    createStore(state, store) {
        state.status = 'CREATE'
        state.storesList = Object.assign({ [store.id]: store }, state.storesList)
    },
    updateStore(state, store) {
        state.status = 'UPDATE'
        state.storesList[store.id] = Object.assign({}, state.storesList[store.id], store)
    },
    deleteStore(state, id) {
        state.status = 'DELETE_FROM_LIST'
        delete state.storesList[id]
    },
    error(state) {
        state.status = 'ERROR'
    },
    setTags(state, tags) {
        state.status = 'SET_TAGS'
        state.tags = Object.assign({}, tags)
    },
    clearFetched(state) {
        state.storesList = {}
    }
}

const getters = {
    status: state => state.status,
    stores: state => state.storesList,
    firstStore: state => getFirstOfArray(state.storesList) || {},
    storesCount: state => Object.keys(state.storesList).length,
    selectableStores: (state, getters) => {
        return Object.keys(getters.stores).map((id) => {
            return {
                value: id,
                label: id + '-' + getters.stores[id].name,
            }
        })
    },
    selectableActiveStores: (state, getters) => {
        return Object.keys(getters.stores).filter(id => getters.stores[id].status === StoreStatus.ACTIVE).map((id) => {
            return {
                value: id,
                label: getters.stores[id].name,
            }
        })
    },
    selectableStoresWithNullStart: (state, getters) => {
        return [
            {
                label: i18n.t('select.placeholder'),
                value: ''
            },
            ...getters.selectableStores
        ]
    },
    selectableActiveStoresWithNullStart: (state, getters) => {
        return [
            {
                label: i18n.t('select.placeholder'),
                value: ''
            },
            ...getters.selectableActiveStores
        ]
    },
    storeById: state => id => state.storesList[id],
    params: state => state.params,
    pagination: state => state.pagination,
    tags: state => state.tags,
    arrayTags: state => {
        return Object.keys(state.tags).map(id => {
            return state.tags[id]
        })
    },
    tagsInput: state => {
        return Object.keys(state.tags).map(id => {
            return {
                text: state.tags[id].name
            }
        })
    }
}

const actions = {
    setParamsAndFetch({ dispatch }, params) {
        dispatch('setParams', params)
        dispatch('fetch')
    },
    setParams({ commit }, params) {
        commit('setParams', params)
    },
    resetParams({ commit }) {
        commit('resetParams')
    },
    fetch({ commit, state }, params) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.fetch(params)
                .then(response => {
                    const result = response.data
                    const stores = groupByArrayOfObject(result.data, 'id')

                    commit('setStoresList', stores)
                    if (params.no_paginate) {
                        commit('setPagination', {
                            no_paginate: true
                        })
                    } else {
                        commit('setPagination', {
                            per_page: result.per_page,
                            current_page: result.current_page,
                            last_page: result.last_page,
                            no_paginate: false,
                        })
                        commit('setParams', {
                            page: result.current_page,
                        })
                    }
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    fetchPush({ commit, state }, params) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.fetch(params)
                .then(response => {
                    const result = response.data
                    const stores = groupByArrayOfObject(result.data, 'id')

                    commit('pushStoresList', stores)
                    if (params.no_paginate) {
                        commit('setPagination', {
                            no_paginate: true
                        })
                    } else {
                        commit('setPagination', {
                            per_page: result.per_page,
                            current_page: result.current_page,
                            last_page: result.last_page,
                            no_paginate: false,
                        })
                        commit('setParams', {
                            page: result.current_page,
                        })
                    }
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    fetchForSelect({ commit, state }, params = {}) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.fetchForSelect(params)
                .then(response => {
                    const result = response.data
                    const stores = groupByArrayOfObject(result.data, 'id')

                    commit('setStoresList', stores)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    find({ commit }, id) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.find(id)
                .then(response => {
                    const result = response.data
                    const store = result.data
                    const stores = {}
                    stores[store.id] = store

                    commit('setStoresList', stores)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    clearFetched({commit}) {
        commit('clearFetched')
    },
    create({ commit }, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.create(params)
                .then(response => {
                    const result = response.data

                    commit('createStore', result.data)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    edit({ commit }, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.edit(params, params.id)
                .then(() => {
                    commit('updateStore', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeStatus({ commit }, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.changeStatus(params.id, params.status)
                .then(() => {
                    commit('updateStore', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    join({ commit }, data) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.join(data.id, data)
                .then(response => {
                    const result = response.data

                    commit('updateStore', {
                        id: result.data.id,
                        user_rel_status: result.data.user_rel_status
                    })
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    stopWorking({ commit }, store_id) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.stopWorking(store_id)
                .then(() => {
                    commit('updateStore', {
                        id: store_id,
                        user_rel_status: StoreUserRelationStatus.STOP_BY_PUBLISHER
                    })
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    testConversion({ commit }, store_id) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.testConversion(store_id)
                .then(response => {
                    const result = response.data
                    const testData = result.data

                    commit('updateStore', {
                        id: store_id,
                        test_conversion_data: testData
                    })
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    fetchTags({ commit }, params = {}) {
        commit('loading')

        return new Promise((resolve, reject) => {
            storeService.getAllTags(params)
                .then(response => {
                    const result = response.data
                    const tags = groupByArrayOfObject(result.data, 'id')

                    commit('setTags', tags)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    }
}

export default {
    namespaced: true,
    state: initialStates,
    mutations: mutations,
    getters: getters,
    actions: actions,
}